const content = `<p>The Strategy design pattern is a direct application of <a href="/polymorphism">Polymorphism</a>. It consists in having classes focused on implementing different versions of the subparts of a function.</p>

<p>Let’s take an example to illustrate.</p>

<p>An email marketing company writes emails to its subscribers. A subscriber has a first name, a last name and can have a title (e.g. “Mr.”, “Mrs.”, “Dr.”, etc.). Here is the <code>Subscriber</code> class:</p>

<pre><code class="language-ruby">class Subscriber
  def initialize(title, first_name, last_name)
    @title = title
    @first_name = first_name
    @last_name = last_name
  end

  attr_reader :title
  attr_reader :first_name
  attr_reader :last_name
end</code></pre>

<p>The company wants to have several tones available in emails: a formal tone and a familiar tone. In the formal tone, emails start with an expression like <code>"Hello Mr. John Doe,"</code> and end with <code>"Best regards."</code>. In the familiar tone, emails start with an expression like <code>"Hey John,"</code> and finish with <code>"Cheers!"</code>.</p>

<p>The email has subparts that depend on the tone. The Strategy pattern consists in using Polymorphism to separate the code of the email from the code of the tones.</p>

<p>In our example, the interface that the tones implement is composed of two functions: a <code>greeting</code> function that takes a subscriber and returns a string and a <code>closing</code> function that just returns a string.</p>

<p>The code of the email calls this interface:</p>

<pre><code class="language-ruby">def compose_email_to(subscriber, tone)
  &lt;&lt;~EMAIL
  #{tone.greetings(subscriber)},
  I hope all is well with you.
  #{tone.closing}
  EMAIL
end</code></pre>

<p>The formal tone is represented by a class, called a strategy, that implements this interface:</p>

<pre><code class="language-ruby">class FormalTone
  def self.greetings(subscriber)
    "Hello #{subscriber.title} #{subscriber.first_name} #{subscriber.last_name}"
  end

  def self.closing
    'Best regards'
  end
end</code></pre>

<p>And the familiar tone is also represented by its own strategy:</p>

<pre><code class="language-ruby">class FamiliarTone
  def self.greetings(subscriber)
    "Hey #{subscriber.first_name}"
  end

  def self.closing
    'See you!'
  end
end</code></pre>

<p>This achieves <a href="/low-coupling">low coupling</a> between the code of the email and the code of the tones. Indeed, the code of the email know nothing about the code of the strategies apart from the fact they implement the interface for tones (<code>greetings</code> and <code>closing</code>). This let us change the code of the strategies without changing the code of the email.</p>

<p>We can also add a new strategy that the email will work with, without changing the code of the email. For example a caring tone:</p>

<pre><code class="language-ruby">class CaringtTone
  def self.greetings(subscriber)
    "My dear #{subscriber.first_name}"
  end

  def self.closing
    'With all my affection'
  end
end</code></pre>

<p>By passing a <code>tone</code> of this type, the email will use its greetings and closing without needing to change its code. Strategy uses <a href="/polymorphism">Polymorphism</a> allows to decouple callers from callees by making them rely only on an interface.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Strategy allows code to have multiple versions of some of its subparts</li><li>It introduces an interface between the code and its varying subparts and creates Polymorphism</li><li>It allows to change the code of the subparts, or add new types of subparts without changing the code that uses them</li></ul>
</div>`

export default content
